import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { emailFormHintSelector, setEmailFormHint } from '@store/slices/form-hints.slice';
import { languageSelector } from '@store/slices/common.slice';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { LanguageCode } from '@models';
import { RxUtils } from '@utils';
import { authService, HttpError, HttpStatusCode, loadingSpinnerOverlayService } from '@services';
import { Box, Link, Stack, Typography } from '@mui/material';
import {
	AuthLayout,
	FormikPassword,
	FormikSubmitButton,
	FormikTextfield,
	FormikTextFieldProps,
	RouterLinkAnchor
} from '@components/common';
import { WavingHandIcon } from '@components/icons';

type FormFields = Extendable<{
	email: string;
	password: string;
}>;

const InputStyle: FormikTextFieldProps['sx'] = { minWidth: 300, maxWidth: 300 };

export const Login: FC = () => {
	const { t } = useTranslation('common');
	const dispatch = useDispatch();
	const emailFormHint = useSelector(emailFormHintSelector);
	const language = useSelector(languageSelector);
	const navigate = useNavigate();
	const navigateToPortal = () => navigate('/portal');
	const loginUrl = useMemo(() => {
		return language === LanguageCode.FR ? 'https://diagnosticve.voltaage.io/f0tifjrft' : 'https://diagnosticve.voltaage.io/f24wo8iff';
	}, [language]);

	useEffect(() => {
		loadingSpinnerOverlayService.reset();
		RxUtils.promisify(authService.refresh(false), navigateToPortal);
	}, []);

	const formik = useFormik<FormFields>({
		initialValues: { email: emailFormHint || '', password: '' },
		validateOnChange: true,
		validationSchema: yup.object({
			email: yup.string().email(t('validation.email-invalid')).required(t('validation.email-required')),
			password: yup.string().min(8, t('validation.password-min-char-len')).required(t('validation.password-required'))
		}),
		onSubmit: ({ email, password }, api) => {
			RxUtils.promisify(
				authService.login(email, password),
				navigateToPortal,
				(err) => {
					if (err instanceof HttpError && err.statusCode === HttpStatusCode.PRECONDITION_FAILED) {
						navigate(`/change-password?email=${email}`);
					} else if (err instanceof HttpError && err.statusCode === HttpStatusCode.PRECONDITION_REQUIRED) {
						navigate(`/verify-user/${email}`);
					} else {
						return undefined;
					}
				},
				() => api.setSubmitting(false)
			);
		}
	});

	return (
		<AuthLayout>
			<Box component="form" onSubmit={formik.handleSubmit} sx={{ flexGrow: 1 }}>
				<Stack direction="column" alignItems="center" justifyContent="center" spacing={{ xs: 4, md: 10 }} flexGrow={1}>
					<Stack direction="column" alignItems="center">
						<WavingHandIcon sx={{ width: 48, height: 48 }} />
						<Typography variant="h2" component="h1" sx={{ marginTop: 4 }}>
							{t('login.title')}
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ marginTop: 2 }}>
							<Typography>{t('login.not-a-member')}</Typography>
							<Link href={loginUrl} target='_blank' data-testid="register-link">
								<Typography>{t('login.register-link')}</Typography>
							</Link>
						</Stack>
					</Stack>
					<Stack direction={{ xs: 'column', md: 'row' }} alignItems="flex-start" spacing={4}>
						<FormikTextfield
							formikApi={formik}
							name="email"
							title={t('email')}
							placeholder={t('email')}
							sx={InputStyle}
							onChange={(e) => dispatch(setEmailFormHint(e.target.value))}
						/>
						<FormikPassword
							formikApi={formik}
							name="password"
							title={t('password')}
							placeholder={t('password')}
							sx={InputStyle}
						/>
					</Stack>

					<Stack direction="column" alignItems="center" spacing={{ xs: 2, md: 5 }}>
						<FormikSubmitButton color="primary" formik={formik} data-testid="login-button">
							{t('login.login-button')}
						</FormikSubmitButton>
						<RouterLinkAnchor href="/forgot-password" data-testid="forget-password-link">
							<Typography variant="body1">{t('login.forgot-password-link')}</Typography>
						</RouterLinkAnchor>
					</Stack>
				</Stack>
			</Box>
		</AuthLayout>
	);
};
